// Spanish editions of the blog posts. Each post lives in
// src/content/posts-es/<base>.meta.json (metadata) and <base>.html (body),
// mirroring src/lib/posts.ts. Every meta carries `enSlug` pointing at its
// English counterpart so both routes can emit hreflang alternates.

import type { PostMeta } from "./posts";

export type EsPostMeta = PostMeta & {
  /** Slug of the English counterpart post. */
  enSlug: string;
  inLanguage?: string;
};

export type EsPost = EsPostMeta & {
  contentHtml: string;
};

const metaModules = import.meta.glob<EsPostMeta>(
  "../content/posts-es/*.meta.json",
  { eager: true, import: "default" },
);

const htmlModules = import.meta.glob<string>("../content/posts-es/*.html", {
  query: "?raw",
  import: "default",
});

function fileBase(path: string, suffix: string): string {
  const name = path.slice(path.lastIndexOf("/") + 1);
  return name.endsWith(suffix) ? name.slice(0, -suffix.length) : name;
}

const htmlLoaderBySlug: Record<string, () => Promise<string>> = {};
for (const [metaPath, meta] of Object.entries(metaModules)) {
  const base = fileBase(metaPath, ".meta.json");
  const htmlPath = Object.keys(htmlModules).find(
    (k) => fileBase(k, ".html") === base,
  );
  if (htmlPath) htmlLoaderBySlug[meta.slug] = htmlModules[htmlPath];
}

const allMeta: EsPostMeta[] = Object.values(metaModules).sort(
  (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
);

const metaBySlug: Record<string, EsPostMeta> = Object.fromEntries(
  allMeta.map((p) => [p.slug, p]),
);

export function listEsPosts(): EsPostMeta[] {
  return allMeta;
}

export function getEsPostMetaBySlug(slug: string): EsPostMeta | null {
  return metaBySlug[slug] ?? null;
}

export function getAdjacentEsPosts(slug: string): {
  prev: EsPostMeta | null;
  next: EsPostMeta | null;
} {
  const idx = allMeta.findIndex((p) => p.slug === slug);
  if (idx === -1) return { prev: null, next: null };
  return {
    prev: idx > 0 ? allMeta[idx - 1] : null,
    next: idx < allMeta.length - 1 ? allMeta[idx + 1] : null,
  };
}

export function prefetchEsPostHtml(slug: string): void {
  if (htmlCache.has(slug)) return;
  const loader = htmlLoaderBySlug[slug];
  if (!loader) return;
  const p = loader().catch((err) => {
    htmlCache.delete(slug);
    throw err;
  });
  htmlCache.set(slug, p);
}

const htmlCache = new Map<string, Promise<string>>();

export async function getEsPostBySlug(slug: string): Promise<EsPost | null> {
  const meta = metaBySlug[slug];
  if (!meta) return null;
  let htmlPromise = htmlCache.get(slug);
  if (!htmlPromise) {
    const loader = htmlLoaderBySlug[slug];
    if (!loader) return null;
    htmlPromise = loader().catch((err) => {
      htmlCache.delete(slug);
      throw err;
    });
    htmlCache.set(slug, htmlPromise);
  }
  const contentHtml = await htmlPromise;
  return { ...meta, contentHtml };
}
